import React, { useEffect } from 'react';
import {
  Box, VStack, Text, Flex, Icon, Divider, Button, useColorModeValue,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Image, Avatar, HStack, CloseButton, IconButton, Drawer, DrawerOverlay, DrawerContent,
  useDisclosure, useBreakpointValue
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MdDashboard, MdLogout, MdMenu, MdPeople, MdSettings,
  MdAssessment, MdFolder, MdNotifications, MdTableChart, MdEmail, MdGroupWork, MdAttachMoney, MdBarChart, MdCalculate
} from 'react-icons/md';
import { FaChevronRight } from 'react-icons/fa';
import './AdminSidebar.css';

const SidebarItem = ({ icon, label, to, isActive }) => {
  const navigate = useNavigate();
  const activeBg = useColorModeValue('brand.lightBlue', 'rgba(25, 118, 210, 0.2)');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const activeTextColor = useColorModeValue('brand.primary', 'brand.secondary');
  const isMobile = useBreakpointValue({ base: true, lg: false });

  return (
    <Flex
      align="center"
      p={{ base: 4, lg: 3 }}
      mx={{ base: 0, lg: 2 }}
      borderRadius={{ base: "md", lg: "lg" }}
      role="group"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeTextColor : textColor}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        color: isActive ? activeTextColor : textColor,
      }}
      transition="all 0.2s"
      onClick={() => {
        to && navigate(to);
        if (isMobile && window.onCloseMobileSidebar) {
          window.onCloseMobileSidebar();
        }
      }}
    >
      <Icon 
        as={icon} 
        fontSize={{ base: "24px", lg: "20px" }}
        mr="3"
        color={isActive ? activeTextColor : 'gray.500'}
      />
      <Text fontSize={{ base: "md", lg: "sm" }} fontWeight={isActive ? "semibold" : "medium"}>{label}</Text>
      {isActive && (
        <Icon 
          as={FaChevronRight} 
          fontSize={{ base: "16px", lg: "14px" }}
          ml="auto" 
          color={activeTextColor}
        />
      )}
    </Flex>
  );
};

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  // Make onClose available globally for mobile
  useEffect(() => {
    if (isMobile) {
      window.onCloseMobileSidebar = onClose;
    }
    return () => {
      if (window.onCloseMobileSidebar) {
        delete window.onCloseMobileSidebar;
      }
    };
  }, [isMobile, onClose]);

  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const bgSidebar = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const logoSrc = useColorModeValue(
    '/Assets/Logos/logo_mini.png',
    '/Assets/Logos/Logo-night.png'
  );

  const activeTextColor = useColorModeValue('brand.primary', 'brand.secondary');

  const logout = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isActiveParent = (prefix) => {
    return location.pathname.startsWith(prefix);
  };
  const isActiveBA = (subPath) => {
    return location.pathname === `/admin/precatorios/ba/${subPath}`;
  };

  const isActiveMA = (subPath) => {
    return location.pathname === `/admin/precatorios/ma/${subPath}`;
  };

  const isActivePB = (subPath) => {
    return location.pathname === `/admin/precatorios/pb/${subPath}`;
  };

  const isActiveRN = (subPath) => {
    return location.pathname === `/admin/precatorios/rn/${subPath}`;
  };

  const isActiveRS = (subPath) => {
    return location.pathname === `/admin/precatorios/rs/${subPath}`;
  };

  const isActivePR = (subPath) => {
    return location.pathname === `/admin/precatorios/pr/${subPath}`;
  };

  const isActiveRJ = (subPath) => {
    return location.pathname === `/admin/precatorios/rj/${subPath}`;
  };

  const isActiveSP = (subPath) => {
    return location.pathname === `/admin/precatorios/sp/${subPath}`;
  };

  const isActiveAC = (subPath) => {
    return location.pathname === `/admin/precatorios/ac/${subPath}`;
  };

  const isActiveAL = (subPath) => {
    return location.pathname === `/admin/precatorios/al/${subPath}`;
  };

  const isActiveAM = (subPath) => {
    return location.pathname === `/admin/precatorios/am/${subPath}`;
  };

  const isActiveMT = (subPath) => {
    return location.pathname === `/admin/precatorios/mt/${subPath}`;
  };

  const isActiveMS = (subPath) => {
    return location.pathname === `/admin/precatorios/ms/${subPath}`;
  };

  const isActiveMG = (subPath) => {
    return location.pathname === `/admin/precatorios/mg/${subPath}`;
  };

  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');

  const SidebarContent = () => (
    <Box 
      bg={bgSidebar} 
      w={{ base: "100%", lg: "250px" }}
      h="100vh" 
      py="5" 
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      position={{ base: "relative", lg: "fixed" }}
      left="0"
      top="0"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
      shadow={{ base: "none", lg: "lg" }}
      transition="all 0.2s"
      zIndex="1000"
    >
      <Flex 
        direction="column" 
        h="full" 
        justify="space-between"
      >
        <Box>
          <Flex 
            mb="3" 
            px="75" 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Box maxW="100%">
              <Image 
                src={logoSrc}
                alt="Logo"
                maxH="75px"
                objectFit="contain"
              />
            </Box>
            {isMobile && (
              <CloseButton onClick={onClose} size="md" />
            )}
          </Flex>
          
          <HStack mb="6" px="5" spacing={3}>
            <Avatar 
              size="sm" 
              name={userInfo?.name || 'Administrador'}
              src={userInfo?.avatar || ''}
              bg="purple.500"
              color="white"
            />
            <Box>
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {userInfo?.name || 'Administrador'}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.500"
                noOfLines={1}
              >
                Administrador
              </Text>
            </Box>
          </HStack>
          
          <Divider mb="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Menu Principal
          </Text>

          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDashboard} 
                label="Dashboard" 
                to="/admin/dashboard" 
                isActive={isActive('/admin/dashboard')}
              />
            </Box>
            {/* Nested Accordion for Bahia with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/ba') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/ba') ? "semibold" : "medium"}>
                      Bahia
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/ba" 
                        isActive={isActive('/admin/precatorios/ba')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/ba/selecionadas" 
                        isActive={isActiveBA('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Pendentes-Potenciais" 
                        to="/admin/precatorios/ba/pendentes" 
                        isActive={isActiveBA('pendentes')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/ba/alto-valor" 
                        isActive={isActiveBA('alto-valor')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Maranhão with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/ma') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/ma') ? "semibold" : "medium"}>
                      Maranhão
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/ma" 
                        isActive={isActive('/admin/precatorios/ma')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/ma/selecionadas" 
                        isActive={isActiveMA('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/ma/alto-valor" 
                        isActive={isActiveMA('alto-valor')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Pendentes" 
                        to="/admin/precatorios/ma/pendentes" 
                        isActive={isActiveMA('pendentes')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Paraíba with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/pb') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/pb') ? "semibold" : "medium"}>
                      Paraíba
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Inicial/Total"
                        to="/admin/precatorios/pb"
                        isActive={isActive('/admin/precatorios/pb')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Selecionadas"
                        to="/admin/precatorios/pb/selecionadas"
                        isActive={isActivePB('selecionadas')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes-Potenciais"
                        to="/admin/precatorios/pb/pendentes"
                        isActive={isActivePB('pendentes')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Alto Valor"
                        to="/admin/precatorios/pb/alto-valor"
                        isActive={isActivePB('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Sincronizar Dados"
                        to="/admin/precatorios/pb/sincronizar"
                        isActive={isActivePB('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Rio Grande do Norte with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/rn') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/rn') ? "semibold" : "medium"}>
                      Rio Grande do Norte
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Inicial/Total"
                        to="/admin/precatorios/rn"
                        isActive={isActive('/admin/precatorios/rn')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Selecionadas"
                        to="/admin/precatorios/rn/selecionadas"
                        isActive={isActiveRN('selecionadas')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Alto Valor"
                        to="/admin/precatorios/rn/alto-valor"
                        isActive={isActiveRN('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes"
                        to="/admin/precatorios/rn/pendentes"
                        isActive={isActiveRN('pendentes')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Rio Grande do Sul with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/rs') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/rs') ? "semibold" : "medium"}>
                      Rio Grande do Sul
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/rs" 
                        isActive={isActive('/admin/precatorios/rs')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/rs/selecionadas" 
                        isActive={isActiveRS('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/rs/alto-valor" 
                        isActive={isActiveRS('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes"
                        to="/admin/precatorios/rs/pendentes"
                        isActive={isActiveRS('pendentes')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Sincronizar Dados"
                        to="/admin/precatorios/rs/sincronizar"
                        isActive={isActiveRS('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Paraná with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/pr') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/pr') ? "semibold" : "medium"}>
                      Paraná
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Inicial/Total"
                        to="/admin/precatorios/pr"
                        isActive={isActive('/admin/precatorios/pr')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Selecionadas"
                        to="/admin/precatorios/pr/selecionadas"
                        isActive={isActivePR('selecionadas')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes-Potenciais"
                        to="/admin/precatorios/pr/pendentes"
                        isActive={isActivePR('pendentes')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Alto Valor"
                        to="/admin/precatorios/pr/alto-valor"
                        isActive={isActivePR('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Sincronizar Dados"
                        to="/admin/precatorios/pr/sincronizar"
                        isActive={isActivePR('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Rio de Janeiro with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/rj') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/rj') ? "semibold" : "medium"}>
                      Rio de Janeiro
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/rj" 
                        isActive={isActive('/admin/precatorios/rj')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/rj/selecionadas" 
                        isActive={isActiveRJ('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/rj/alto-valor" 
                        isActive={isActiveRJ('alto-valor')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Pendentes" 
                        to="/admin/precatorios/rj/pendentes" 
                        isActive={isActiveRJ('pendentes')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for São Paulo with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/sp') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/sp') ? "semibold" : "medium"}>
                      São Paulo
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/sp" 
                        isActive={isActive('/admin/precatorios/sp')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/sp/selecionadas" 
                        isActive={isActiveSP('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/sp/alto-valor" 
                        isActive={isActiveSP('alto-valor')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Pendentes" 
                        to="/admin/precatorios/sp/pendentes" 
                        isActive={isActiveSP('pendentes')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Acre with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/ac') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/ac') ? "semibold" : "medium"}>
                      Acre
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/ac" 
                        isActive={isActive('/admin/precatorios/ac')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Alagoas with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/al') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/al') ? "semibold" : "medium"}>
                      Alagoas
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/al" 
                        isActive={isActive('/admin/precatorios/al')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Amazonas with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/am') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton 
                  p="2" 
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{ 
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/am') ? "semibold" : "medium"}>
                      Amazonas
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Inicial/Total" 
                        to="/admin/precatorios/am" 
                        isActive={isActive('/admin/precatorios/am')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Selecionadas" 
                        to="/admin/precatorios/am/selecionadas" 
                        isActive={isActiveAM('selecionadas')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Alto Valor" 
                        to="/admin/precatorios/am/alto-valor" 
                        isActive={isActiveAM('alto-valor')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Pendentes" 
                        to="/admin/precatorios/am/pendentes" 
                        isActive={isActiveAM('pendentes')}
                      />
                      <SidebarItem 
                        icon={() => <Box w="5px" />} 
                        label="Sincronizar Dados" 
                        to="/admin/precatorios/am/sincronizar" 
                        isActive={isActiveAM('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Mato Grosso with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/mt') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/mt') ? "semibold" : "medium"}>
                      Mato Grosso
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Inicial/Total"
                        to="/admin/precatorios/mt"
                        isActive={isActive('/admin/precatorios/mt')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Selecionadas"
                        to="/admin/precatorios/mt/selecionadas"
                        isActive={isActiveMT('selecionadas')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Alto Valor"
                        to="/admin/precatorios/mt/alto-valor"
                        isActive={isActiveMT('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes"
                        to="/admin/precatorios/mt/pendentes"
                        isActive={isActiveMT('pendentes')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Sincronizar Dados"
                        to="/admin/precatorios/mt/sincronizar"
                        isActive={isActiveMT('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Mato Grosso do Sul with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/ms') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/ms') ? "semibold" : "medium"}>
                      Mato Grosso do Sul
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                  <AccordionPanel pb={2} pt={1} pl={6}>
                    <VStack align="stretch" spacing={1}>
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Inicial/Total"
                        to="/admin/precatorios/ms"
                        isActive={isActive('/admin/precatorios/ms')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Selecionadas"
                        to="/admin/precatorios/ms/selecionadas"
                        isActive={isActiveMS('selecionadas')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Alto Valor"
                        to="/admin/precatorios/ms/alto-valor"
                        isActive={isActiveMS('alto-valor')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Pendentes"
                        to="/admin/precatorios/ms/pendentes"
                        isActive={isActiveMS('pendentes')}
                      />
                      <SidebarItem
                        icon={() => <Box w="5px" />}
                        label="Sincronizar Dados"
                        to="/admin/precatorios/ms/sincronizar"
                        isActive={isActiveMS('sincronizar')}
                      />
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
            </Accordion>
            {/* Nested Accordion for Minas Gerais with sub-pages */}
            <Accordion allowToggle defaultIndex={isActiveParent('/admin/precatorios/mg') ? 0 : null}>
              <AccordionItem border="none">
                <AccordionButton
                  p="2"
                  pl="8"
                  borderRadius="lg"
                  _hover={{ bg: hoverBg }}
                  _expanded={{
                    color: activeTextColor,
                    fontWeight: "semibold"
                  }}
                >
                  <Flex align="center" flex="1" textAlign="left">
                    <Text fontWeight={isActiveParent('/admin/precatorios/mg') ? "semibold" : "medium"}>
                      Minas Gerais
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={2} pt={1} pl={6}>
                  <VStack align="stretch" spacing={1}>
                    <SidebarItem
                      icon={() => <Box w="5px" />}
                      label="Inicial/Total"
                      to="/admin/precatorios/mg"
                      isActive={isActive('/admin/precatorios/mg')}
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdPeople}
                label="Gerenciar Usuários"
                to="/admin/users"
                isActive={isActive('/admin/users')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdAssessment} 
                label="Atribuir Precatórios" 
                to="/admin/precatorio-assignment" 
                isActive={isActive('/admin/precatorio-assignment')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdFolder} 
                label="Clientes" 
                to="/admin/clients" 
                isActive={isActive('/admin/clients')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdTableChart}
                label="Tabela RCL"
                to="/admin/tabela-RCL"
                isActive={isActive('/admin/tabela-RCL')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdAttachMoney}
                label="Taxas e Valores"
                to="/admin/taxas-valores"
                isActive={isActive('/admin/taxas-valores')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdCalculate}
                label="Calculadora"
                to="/admin/calculadora"
                isActive={isActive('/admin/calculadora')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdEmail}
                label="Enviar Emails Estados" 
                to="/admin/email" 
                isActive={isActive('/admin/email')}
              />
              <SidebarItem 
                icon={MdEmail} 
                label="Testar Email" 
                to="/admin/email-test" 
                isActive={isActive('/admin/email-test')}
              />
              <SidebarItem 
                icon={MdEmail} 
                label="Importar Emails OAB" 
                to="/admin/oab-email-import" 
                isActive={isActive('/admin/oab-email-import')}
              />
              <SidebarItem 
                icon={MdEmail} 
                label="Controle de Encaminhamento" 
                to="/admin/email-forwarding" 
                isActive={isActive('/admin/email-forwarding')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdGroupWork} 
                label="Agrupamento Precatórios" 
                to="/admin/precatorio-groups" 
                isActive={isActive('/admin/precatorio-groups')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdAssessment}
                label="Relatórios"
                to="/admin/reports"
                isActive={isActive('/admin/reports')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdBarChart}
                label="Estatísticas por Valor"
                to="/admin/precatorio-statistics"
                isActive={isActive('/admin/precatorio-statistics')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem
                icon={MdNotifications}
                label="Notificações"
                to="/admin/notifications"
                isActive={isActive('/admin/notifications')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdSettings} 
                label="Configurações" 
                to="/admin/settings" 
                isActive={isActive('/admin/settings')}
              />
            </Box>
          </VStack>
        </Box>

        <Box mt="auto" px="4" mb="4">
          <Button 
            leftIcon={<MdLogout />}
            onClick={logout}
            variant="outline"
            width="100%"
            justifyContent="flex-start"
            fontWeight="normal"
            size={{ base: "md", lg: "sm" }}
            py={{ base: 6, lg: 4 }}
          >
            Sair
          </Button>
          <Text 
            fontSize="xs" 
            color="gray.500" 
            textAlign="center" 
            mt="3"
          >
            © 2025 MICA Capital
          </Text>
        </Box>
      </Flex>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            aria-label="Menu"
            icon={<MdMenu />}
            position="fixed"
            top="4"
            left="4"
            zIndex="1000"
            colorScheme="purple"
            onClick={onOpen}
            size="lg"
            display={{ base: "flex", lg: "none" }}
            boxShadow="md"
          />
          <Drawer
            isOpen={isOpen}
            placement="left"
            onClose={onClose}
            returnFocusOnClose={false}
          >
            <DrawerOverlay />
            <DrawerContent maxW="280px">
              <SidebarContent />
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <SidebarContent />
      )}
    </>
  );
};

export default AdminSidebar;
